import { useMemo } from "react";
import DataTable from "../components/table/ServerSideTable";
import { useMaterialsPaginated } from "../hooks/material.hooks";
import type { ColumnDef } from "@tanstack/react-table";
import { type Material, MaterialStatus } from "../types/material.types";
import { format } from "date-fns";
import {
	Button,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Text,
} from "@chakra-ui/react";
import MoreIcon from "../components/icons/MoreIcon";
import ProductBadge from "../components/ProductBadge";
import { useFile } from "../hooks/file.hooks";
import { Activity, Moon, Pen } from "lucide-react";

export type MaterialTabProps = {
	onUpdate?: (material: Material) => void;
	onToggleHistory?: (material: Material) => void;
	onUpdateStatus?: (material: Material, status: MaterialStatus) => void;
};

const MaterialFile = ({
	fileId,
}: {
	fileId: string | number | null | undefined;
}) => {
	const { data: file } = useFile({ id: fileId });

	if (file === undefined || file === null) {
		return null;
	}
	return (
		<img
			src={file.url}
			alt={file.fileName}
			className="w-10 h-10 aspect-square"
		/>
	);
};

const MaterialTab = ({
	onUpdate,
	onToggleHistory,
	onUpdateStatus,
}: MaterialTabProps) => {
	const columns = useMemo<ColumnDef<Material>[]>(() => {
		return [
			{
				cell: ({ row }) => (
					<Text fontSize="12px" fontWeight={500}>
						{row.original.id}
					</Text>
				),
				id: "id",
				header: "ID",
				accessorKey: "id",
				sortDescFirst: true,
			},
			{
				cell: ({ row }) => (
					<div className="flex flex-row gap-2">
						<MaterialFile fileId={row.original.fileId} />
						<Text fontSize="12px" fontWeight={500}>
							{row.original.label}
						</Text>
					</div>
				),
				id: "label",
				header: "Nom",
				accessorKey: "label",
				sortDescFirst: true,
			},
			{
				cell: ({ row }) => (
					<Text fontSize="12px" fontWeight={500}>
						{row.original.updatedAt
							? format(row.original.updatedAt, "dd/MM/yyyy")
							: "---"}
					</Text>
				),
				id: "updatedAt",
				header: "Dernière modification",
				accessorKey: "updatedAt",
				sortDescFirst: true,
			},
			{
				cell: ({ row }) => (
					<ProductBadge status={row.original.status}></ProductBadge>
				),
				id: "status",
				header: "",
				accessorKey: "status",
				sortDescFirst: true,
			},
			{
				cell: ({ row }) => (
					<Text fontSize="12px" fontWeight={500}>
						{row.original.number}
					</Text>
				),
				id: "number",
				header: "Numéro",
				accessorKey: "number",
				sortDescFirst: true,
			},
			{
				cell: ({ row }) => (
					<Menu>
						<MenuButton as={Button} variant="outlined" transition="all 0.2s">
							<MoreIcon w="24px" h="24px" />
						</MenuButton>
						<MenuList>
							<MenuItem
								lineHeight={"24px"}
								onClick={() => onUpdate && onUpdate(row.original)}
								className="flex flex-row items-center gap-2"
							>
								<Pen size="12px" />
								<Text fontSize="14px" fontWeight={500}>
									Modifier
								</Text>
							</MenuItem>
							<MenuItem
								lineHeight={"24px"}
								onClick={() => onToggleHistory && onToggleHistory(row.original)}
								className="flex flex-row items-center gap-2"
							>
								<Activity size="12px" />
								<Text fontSize="14px" fontWeight={500}>
									Voir l'historique
								</Text>
							</MenuItem>
							<MenuItem
								lineHeight={"24px"}
								onClick={() =>
									onUpdateStatus &&
									onUpdateStatus(
										row?.original,
										row?.original?.status === MaterialStatus.ACTIVE
											? MaterialStatus.INACTIVE
											: MaterialStatus.ACTIVE,
									)
								}
								className="flex flex-row items-center gap-2"
							>
								<Moon size="12px" />
								<Text fontSize="14px" fontWeight={500}>
									{row.original.status === MaterialStatus.ACTIVE
										? "Mettre en sommeil"
										: "Activer"}
								</Text>
							</MenuItem>
						</MenuList>
					</Menu>
				),
				id: "actions",
				header: "",
				accessorKey: "actions",
				sortDescFirst: true,
			},
		];
	}, []);
	return (
		<>
			<DataTable
				paginatedQuery={useMaterialsPaginated}
				columns={columns}
				w={["50px", "100px", "190px", "120px", "100px", "4px"]}
				flexGrow={[0, 3, 0, 0, 6, 1]}
			></DataTable>
		</>
	);
};

export default MaterialTab;
